import { Card, CardBody, CardHeader, CardHeaderActions, CardHeading, Empty } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import {
	ArcElement,
	BarController,
	BarElement,
	CategoryScale,
	Chart,
	DoughnutController,
	LinearScale,
	Tooltip,
} from "chart.js";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { eventService } from "../shared/api/event/eventService";
import { AttributeType, EventAnalyticsFilterValues, EventAnalyticsResource } from "./analyticsTypes";
import { EventAnalyticsAttributeMultiSelectControl } from "./AttributeMultiSelect";
import { CountByRatingChart } from "./CountByRatingChart";
import { DoughnutChart } from "./DoughnutChart";
import { mapAnalyticsFilterValuesToRequest } from "./shared";
import { Summary } from "./Summary";

const DoughnutGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1px;
	align-items: start;
`;

const ChartCardBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1px;
	background-color: ${({ theme }) => theme.colors.card["background"].border};
`;

const StyledEmptyBody = styled(CardBody)`
	min-height: 150px;
	display: flex;
	justify-content: center;
	align-items: center;
`;

Chart.register(DoughnutController, ArcElement, Tooltip, BarController, BarElement, LinearScale, CategoryScale);

interface Props {
	filter: EventAnalyticsFilterValues;
}

export function AnalyticsForEvents({ filter }: Props) {
	const { t } = useTranslation();
	const [visibleAttributes, setVisibleAttributes] = useState<AttributeType[]>([
		AttributeType.eventType,
		AttributeType.organizationalUnits,
		AttributeType.industries,
		AttributeType.organizer,
		AttributeType.format,
		AttributeType.category,
		AttributeType.targetGroup,
		AttributeType.region,
		AttributeType.tags,
	]);

	const { data } = useQuery({
		queryFn: () => eventService.analytics(mapAnalyticsFilterValuesToRequest(filter)).then((r) => r.data.data),
		queryKey: [...eventService.analyticsKey, filter],
	});

	return (
		<>
			{data && <Summary data={data} />}
			<Card>
				<CardHeader>
					<CardHeading>{t("Events")}</CardHeading>
					<CardHeaderActions>
						<EventAnalyticsAttributeMultiSelectControl
							name="attributes"
							onChange={(e) => setVisibleAttributes(e.target.value)}
							value={visibleAttributes}
						/>
					</CardHeaderActions>
				</CardHeader>
				{data && <EventAnalyticsBody visibleAttributes={visibleAttributes} data={data} />}
			</Card>
		</>
	);
}

function EventAnalyticsBody({
	data,
	visibleAttributes,
}: {
	data: EventAnalyticsResource;
	visibleAttributes: AttributeType[];
}) {
	const { t } = useTranslation();

	if (data.overview.total === 0 || visibleAttributes.length === 0) {
		return (
			<StyledEmptyBody>
				<Empty>{t("No matching events")}</Empty>
			</StyledEmptyBody>
		);
	}

	return (
		<ChartCardBody>
			<DoughnutGrid>
				{visibleAttributes.includes(AttributeType.organizationalUnits) && (
					<DoughnutChart values={data.organisationalUnits} headline={t("Organizational units")} unit="%" />
				)}
				{visibleAttributes.includes(AttributeType.eventType) && (
					<DoughnutChart values={data.eventTypes} headline={t("Event types")} unit="%" />
				)}
				{visibleAttributes.includes(AttributeType.industries) && (
					<DoughnutChart values={data.industries} headline={t("Industries")} unit="%" />
				)}
				{visibleAttributes.includes(AttributeType.organizer) && (
					<DoughnutChart values={data.organizers} headline={t("Organizers")} unit="%" />
				)}
				{visibleAttributes.includes(AttributeType.format) && (
					<DoughnutChart values={data.formats} headline={t("Formats")} unit="%" />
				)}
				{visibleAttributes.includes(AttributeType.category) && (
					<DoughnutChart values={data.categories} headline={t("Event categories")} unit="%" />
				)}
				{visibleAttributes.includes(AttributeType.targetGroup) && (
					<DoughnutChart values={data.targetGroups} headline={t("Target groups")} unit="%" />
				)}
				{visibleAttributes.includes(AttributeType.region) && (
					<DoughnutChart values={data.regions} headline={t("Regions")} unit="%" />
				)}
				{visibleAttributes.includes(AttributeType.tags) && (
					<DoughnutChart values={data.tags} headline={t("Tags")} unit="%" />
				)}
			</DoughnutGrid>
			<CountByRatingChart counts={data.eventCountByRating} />
		</ChartCardBody>
	);
}
